import React from 'react'
import useStateObject from 'admin/hooks/useStateObject'
import useDocument from 'admin/hooks/useDocument'
import InputField from 'admin/components/shared/InputField'
import Modal from 'admin/components/shared/Modal'
import Header from 'admin/components/shared/Header'
import useModal from '../hooks/useModal'
import useBankImport from '../hooks/useBankImport'
import useEffect from '../hooks/useEffect'
import {gql, useQuery} from 'admin/lib/apollo'
import {useParams} from 'react-router-dom'
import {formatDate, formatTime, pluralise} from '../lib/format'
import moment from 'moment-timezone'

const GET_IMPORTS = gql`
  query GetBankImports($fromDate: String) {
    bankImports(fromDate: $fromDate) {
      id
      name
      createdAt
      importedBy
      fromDate
      toDate
      totalCount
      organisation {
        id
        internalName
        internalColor
      }
    }
  }
`

export default () => {
  const {popModal} = useModal()
  const {organisationID} = useParams()
  const {bankImport, errors, create} = useBankImport()

  const months = organisationID.toString() === '42076' ? 6 : 1 // hack for Tokonet to show last 6 months
  const {data: {bankImports = []} = {}} = useQuery(GET_IMPORTS, {
    variables: {fromDate: moment().subtract(months, 'months').format('YYYY-MM-DD')}
  })

  const [form, setForm, resetForm] = useStateObject({source: 'anz'})

  useEffect(() => setForm(bankImport), [bankImport], false)

  const title = 'Import payments'
  useDocument({title})

  const handleCreate = e => {
    create(form, () => handleClose(e))
  }

  const handleClose = e => {
    e.preventDefault()
    resetForm()
    popModal('new-bank-import')
  }

  const handleFileChange = e => {
    setForm({file: e.target.files[0]})
  }

  return (
    <Modal onClose={handleClose} flashId='new-bank-import'>
      <Header title={title}>
        <button className='button is-primary' onClick={handleCreate}>
          Import
        </button>
      </Header>
      <section className='section is-narrow'>
        <div className='container'>
          <form>
            <div className='field is-grouped'>
              <div className='control is-expanded'>
                <InputField
                  name='name'
                  value={form.name || ''}
                  onChange={setForm}
                  errors={errors.name}
                  min={0}
                  autoFocus={true}
                  required
                />
              </div>
              <div className='control'>
                <label className='label is-small' htmlFor='source'>
                  Bank
                </label>
                <span className='select'>
                  <select
                    id='source'
                    name='source'
                    value={form.source}
                    onChange={e => setForm({source: e.target.value})}>
                    <option value='anz'>ANZ</option>
                    <option value='asb'>ASB</option>
                  </select>
                </span>
                {errors.source && <span className='help is-danger'>{errors.source.join(', ')}</span>}
              </div>
              <div className='control is-expanded'>
                <label className='label is-small'>&nbsp;</label>
                <div className={`file is-fullwidth ${form.file ? 'has-name' : ''} ${errors.file ? 'is-danger' : ''}`}>
                  <label className='file-label'>
                    <input
                      accept='text/csv'
                      className='file-input'
                      type='file'
                      onChange={handleFileChange}
                      filename={form.file?.name}
                    />
                    <span className='file-cta'>
                      <span className='file-icon m-0'>
                        <i className='fa fa-upload'></i>
                      </span>
                      {form.file ? null : <span className='file-label ml-4'> Choose a file… </span>}
                    </span>
                    {form.file ? <span className='file-name'>{form.file.name}</span> : null}
                  </label>
                </div>
                {errors.file && <span className='help is-danger'>{errors.file.join(', ')}</span>}
              </div>
            </div>
          </form>
        </div>
      </section>
      {bankImports.length ? (
        <section className='section is-narrow'>
          <div className='container'>
            <h1 className='title is-5'>Last {pluralise('month', months, true)} of imports</h1>
            <table className='table is-fullwidth is-narrow'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Payments</th>
                  <th>Imported by</th>
                  <th>Date range</th>
                  <th>Imported at</th>
                </tr>
              </thead>

              <tbody className='is-size-7'>
                {bankImports.map((bankImport, i) => {
                  return (
                    <tr key={i}>
                      <td>{bankImport.name} </td>
                      <td>{bankImport.totalCount}</td>
                      <td>{bankImport.importedBy}</td>
                      <td>
                        {formatDate(bankImport.fromDate)} - {formatDate(bankImport.toDate)}
                      </td>
                      <td>{formatTime(bankImport.createdAt)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      <section className='section is-narrow has-background-white-bis'>
        <div className='container'>
          <h1 className='title is-5'>How to download and import your bank statement</h1>
          <ol>
            <li>Login to your online banking</li>
            <li>Select the account you want to export</li>
            <li>
              Export transactions <b>as a CSV file (including headers)</b>
            </li>
            <li>
              Come back here, click <b>Choose a file...</b> and select your exported file
            </li>
            <li>
              Click <b>Import</b>
            </li>
          </ol>
        </div>
      </section>
    </Modal>
  )
}
